import { useEffect, useRef } from 'react';

const products = [
  {
    id: 'vpn',
    name: 'BORVIL VPN',
    tagline: 'Your Privacy, Uncompromised',
    description:
      'A professional-grade VPN application for iOS that encrypts your internet connection and protects your identity online. Built with military-grade AES-256 encryption and a strict zero-log policy, it delivers both security and speed.',
    image:
      'https://public.readdy.ai/ai/img_res/cdb3d0c643d933115ec673118d338fc0.jpg',
    features: [
      { icon: 'ri-lock-2-line', text: 'AES-256 Encryption' },
      { icon: 'ri-eye-off-line', text: 'Strict Zero-Log Policy' },
      { icon: 'ri-flashlight-line', text: 'Ultra-Fast Servers' },
      { icon: 'ri-global-line', text: '90+ Server Locations' },
    ],
    accent: 'emerald',
  },
  {
    id: 'cleaner',
    name: 'BORVIL Cleaner',
    tagline: 'Peak Performance, Always',
    description:
      'A powerful iOS optimization tool that intelligently detects and removes junk files, duplicate photos, and app caches — freeing up valuable storage and keeping your device running at its absolute best.',
    image:
      'https://readdy.ai/api/search-image?query=smartphone%20optimization%20cleaner%20app%20interface%20glowing%20sparkle%20effects%20removing%20digital%20debris%20from%20mobile%20phone%20abstract%20minimal%20white%20and%20green%20light%20rays%20particle%20effects%20clean%20bright%20performance%20concept%20futuristic&width=700&height=500&seq=103&orientation=landscape',
    features: [
      { icon: 'ri-delete-bin-line', text: 'Smart Junk Removal' },
      { icon: 'ri-image-2-line', text: 'Duplicate Photo Finder' },
      { icon: 'ri-battery-charge-line', text: 'Battery Optimizer' },
      { icon: 'ri-hard-drive-2-line', text: 'Deep Storage Analysis' },
    ],
    accent: 'emerald',
  },
];

export default function ProductsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('[data-animate]').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0) scale(1)';
              }, i * 180);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="products" ref={sectionRef} className="py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-8">
        <div className="text-center mb-20">
          <div
            data-animate
            className="inline-block text-emerald-600 text-xs font-semibold tracking-widest uppercase mb-5 transition-all duration-700"
            style={{ opacity: 0, transform: 'translateY(20px)', fontFamily: "'Inter', sans-serif" }}
          >
            Our Products
          </div>
          <h2
            data-animate
            className="text-zinc-900 font-bold mb-5 leading-tight transition-all duration-700"
            style={{
              opacity: 0,
              transform: 'translateY(20px)',
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              transitionDelay: '120ms',
            }}
          >
            iOS Applications Built for Real Life
          </h2>
          <p
            data-animate
            className="text-zinc-500 max-w-xl mx-auto leading-relaxed transition-all duration-700"
            style={{ opacity: 0, transform: 'translateY(20px)', fontFamily: "'Inter', sans-serif", transitionDelay: '240ms' }}
          >
            Two focused products. One mission — make your iPhone experience safer, faster, and cleaner.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10" data-product-shop>
          {products.map((product, idx) => (
            <div
              key={product.id}
              data-animate
              className="bg-white rounded-2xl overflow-hidden border border-zinc-100 transition-all duration-700 hover:border-emerald-200 group"
              style={{
                opacity: 0,
                transform: 'translateY(30px) scale(0.98)',
                transitionDelay: `${300 + idx * 200}ms`,
              }}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-zinc-900/20 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <p className="text-emerald-400 text-xs font-semibold tracking-widest uppercase mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {product.tagline}
                  </p>
                  <h3 className="text-white text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {product.name}
                  </h3>
                </div>
              </div>

              <div className="p-8">
                <p className="text-zinc-500 leading-relaxed mb-8 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {product.description}
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {product.features.map((feat) => (
                    <div key={feat.text} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 flex-shrink-0">
                        <i className={`${feat.icon} text-emerald-600 text-base`}></i>
                      </div>
                      <span className="text-zinc-700 text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {feat.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
